"use client";

import { useEffect, useMemo, useRef, useState, memo } from "react";
import { motion, MotionValue, useMotionValueEvent, useScroll, useSpring, useTransform } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { PortableText } from "next-sanity";
import { PortableTextBlock } from "sanity";
import Link from "next/link";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";
import { GROQButton, GROQImage, ITraderStory } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";
import { resolveI18nText, resolveI18nRichtext } from "@/sanity/globals/I18n";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";
import GradualBlur from "@/components/global/GradualBlur";
import { useIsMobile } from "@/hooks/useIsMobile";

// Story card design: Figma "IQ Capital | New UI 2026" node 7172-1094 (desktop) / 5390-17721 (mobile).
// Sizes are Figma px / 8 → rem.

const GAP_REM = 2;
const PEEK_FACTOR = 0.22;

export interface TradersStoriesSectionData {
  chip?: string;
  title?: PortableTextBlock[];
  items?: ITraderStory[];
  button?: GROQButton;
  partnerText?: string;
  language?: string;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

/** Filled arrow used by the mobile "Read the full story" link. */
function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12.75 10.625"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Stroked arrow inside the desktop white CTA button (Figma vector 1194234429). */
function ButtonArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 17.1269 13.4727"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0 6.73633H15.8349M9.74456 12.8267L15.8349 6.73633L9.74456 0.645979"
        stroke="currentColor"
        strokeWidth="1.8271"
      />
    </svg>
  );
}

/** Thin chevron for the mobile pager buttons. */
function ChevronIcon({ direction, className }: { direction: "left" | "right"; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 17.4479 17.4479"
      fill="none"
      xmlns="http://www.w3.org/2000/svg">
      <path
        d={
          direction === "left"
            ? "M10.9054 3.25977L5.81641 8.34874L10.9054 13.4377"
            : "M6.54297 3.25977L11.6319 8.34874L6.54297 13.4377"
        }
        stroke="currentColor"
        strokeWidth="0.727"
      />
    </svg>
  );
}

function TrustpilotStars({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="122"
      height="23"
      fill="none"
      viewBox="0 0 122 23">
      <g clipPath="url(#clip_ts_stories)">
        <path
          fill="#219653"
          d="M22.875 0H0v22.875h22.875zM47.656 0H24.781v22.875h22.875zM72.438 0H49.561v22.875h22.876zM97.219 0H74.344v22.875h22.875zM122 0H99.125v22.875H122z"
        />
        <path
          fill="#fff"
          d="m11.437 15.417 3.48-.882 1.453 4.48zm8.007-5.79H13.32L11.437 3.86 9.555 9.626H3.43l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM36.219 15.417l3.478-.882 1.454 4.48zm8.006-5.79H38.1L36.218 3.86l-1.882 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM61 15.417l3.479-.882 1.453 4.48zm8.006-5.79h-6.124L61 3.86l-1.883 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM85.78 15.417l3.48-.882 1.453 4.48zm8.007-5.79h-6.124L85.781 3.86l-1.883 5.766h-6.123l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM110.562 15.417l3.479-.882 1.454 4.48zm8.006-5.79h-6.123l-1.883-5.767-1.882 5.766h-6.124l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192z"
        />
      </g>
      <defs>
        <clipPath id="clip_ts_stories">
          <path
            fill="#fff"
            d="M0 0h122v22.875H0z"
          />
        </clipPath>
      </defs>
    </svg>
  );
}

// ─── Story content (shared by both card layouts) ─────────────────────────────

function useStoryContent(story: ITraderStory, language?: string) {
  const title = resolveI18nRichtext(story.title, story.titleI18n, language);
  const traderRole = resolveI18nText(story.traderRole, story.traderRoleI18n, language);
  const question = resolveI18nText(story.question, story.questionI18n, language);
  const answer = resolveI18nText(story.answer, story.answerI18n, language);
  const watchInterviewLabel = resolveI18nText(story.watchInterviewLabel, story.watchInterviewLabelI18n, language);

  const link = story.watchInterviewLink ? resolvePageLink(story.watchInterviewLink, language) : null;
  const [finalLink, setFinalLink] = useState("");

  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  const traderImage = (language && story.traderImageI18n?.[language as "de" | "br"]) || story.traderImage;

  return {
    title,
    traderRole,
    question,
    answer,
    ctaLabel: watchInterviewLabel || (story.hasVideo ? "Watch interview" : "Read Full Story"),
    finalLink,
    linkTarget: story.watchInterviewLink?.openInNewTab ? "_blank" : undefined,
    linkRel: story.watchInterviewLink?.openInNewTab ? "noopener noreferrer" : undefined,
    traderImage,
  };
}

type StoryContent = ReturnType<typeof useStoryContent>;

function StoryFigure({
  story,
  content,
  gradient,
  className,
}: {
  story: ITraderStory;
  content: StoryContent;
  gradient?: boolean;
  className?: string;
}) {
  const inner = (
    <>
      {content.traderImage && (
        <GroqImage
          src={content.traderImage as GROQImage}
          alt={story.traderName ?? ""}
          className="!absolute !inset-0 !h-full !w-full !object-cover"
        />
      )}
      {gradient && (
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(3,18,13,0)_45%,rgba(3,18,13,0.8)_100%)]" />
      )}
      {story.hasVideo && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Image
            src="/global/video-play-btn.png"
            alt=""
            width={80}
            height={80}
            className="h-[10rem] w-[10rem]"
          />
        </div>
      )}
    </>
  );

  return (
    <div className={cn("relative w-full shrink-0 overflow-hidden", className)}>
      {content.finalLink ? (
        <Link
          href={content.finalLink}
          target={content.linkTarget}
          rel={content.linkRel}
          className="absolute inset-0">
          {inner}
        </Link>
      ) : (
        inner
      )}
    </div>
  );
}

// ─── Desktop card body ───────────────────────────────────────────────────────

const StoryCardDesktop = memo(function StoryCardDesktop({ story, language }: { story: ITraderStory; language?: string }) {
  const content = useStoryContent(story, language);
  const stats = story.stats ?? [];

  return (
    <div className="flex w-full flex-col border-[0.1rem] border-white/[0.09] bg-white/[0.09] pt-[5rem]">
      <div className="flex items-center gap-[7.5rem] overflow-clip px-[4rem] pt-[0.5rem] pb-[4rem]">
        {/* Left — figure + name/role */}
        <div className="flex w-[66.125rem] shrink-0 flex-col gap-[2.5rem]">
          <StoryFigure
            story={story}
            content={content}
            gradient
            className="h-[39.54rem] border-[0.1rem] border-white/[0.14] bg-[#07261b]"
          />
          <div className="flex flex-col gap-[0.75rem]">
            {story.traderName && (
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.417] font-semibold tracking-[-0.021rem] text-[#f2f5f3]">
                {story.traderName}
              </p>
            )}
            {content.traderRole && (
              <p className="mb-0 font-geist text-[1.75rem] leading-[1.179] text-[#e3e3e3] uppercase">{content.traderRole}</p>
            )}
          </div>
        </div>

        {/* Right — headline, quote, description, CTA */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-[2.875rem]">
          {content.title && (
            <div className="richtext richtext-start w-full prose-headings:mt-0 prose-headings:mb-0 prose-headings:font-geist prose-headings:text-[4.5rem] prose-headings:leading-[1.19] prose-headings:font-medium prose-headings:tracking-[-0.375rem] prose-headings:text-white">
              <PortableText
                value={content.title}
                components={richTextComponent}
              />
            </div>
          )}
          <div className="h-px w-[64.125rem] max-w-full shrink-0 bg-white/10" />
          {content.answer && (
            <div className="border-l-[0.25rem] border-[#afffe8] px-[1.25rem]">
              <p className="mb-0 max-w-[59.25rem] font-geist text-[2.25rem] leading-[1.4] tracking-[-0.1125rem] text-white">
                {content.answer}
              </p>
            </div>
          )}
          {content.question && (
            <p className="mb-0 max-w-[60.75rem] font-geist text-[2.25rem] leading-[1.34] tracking-[-0.0225rem] text-white/70">
              {content.question}
            </p>
          )}
          {content.finalLink && (
            <Link
              href={content.finalLink}
              target={content.linkTarget}
              rel={content.linkRel}
              className="flex items-center gap-[2.284rem] bg-white px-[2.5rem] py-[1.25rem] font-geist text-[2.25rem] leading-[1.5] font-semibold tracking-[-0.1125rem] whitespace-nowrap text-black transition-opacity hover:opacity-80">
              {content.ctaLabel}
              <ButtonArrowIcon className="h-[1.684rem] w-[2.141rem] shrink-0" />
            </Link>
          )}
        </div>
      </div>

      {/* Stats strip */}
      {stats.length > 0 && (
        <div className="grid auto-cols-fr grid-flow-col border-t-[0.1rem] border-white/[0.09] bg-black/[0.16]">
          {stats.map((stat, i) => (
            <div
              key={stat._key ?? i}
              className={cn("flex min-w-0 flex-col px-[6rem] py-[3rem]", i > 0 && "border-l-[0.1rem] border-white/[0.09]")}>
              <p className="mb-0 font-geist text-[3.25rem] leading-[1.1] font-medium tracking-[-0.0325rem] whitespace-nowrap text-white">
                {stat.value}
              </p>
              <p className="mb-0 pt-[0.75rem] font-geist text-[1.625rem] leading-[1.5] text-[#a4b3ab]">
                {resolveI18nText(stat.label, stat.labelI18n, language)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ─── Mobile card body ────────────────────────────────────────────────────────

const StoryCardMobile = memo(function StoryCardMobile({ story, language }: { story: ITraderStory; language?: string }) {
  const content = useStoryContent(story, language);

  return (
    <div className="flex h-full w-full flex-col rounded-[0.28rem] bg-white/10 px-[2rem] py-[4rem] shadow-[0_4.75rem_14.0625rem_rgba(0,54,40,0.23)]">
      {content.title && (
        <div className="richtext richtext-start w-full px-[2rem] pt-[2rem] pb-[2.5rem] prose-headings:mt-0 prose-headings:mb-0 prose-headings:font-geist prose-headings:text-[3.5rem] prose-headings:leading-[1.455] prose-headings:font-normal prose-headings:tracking-[-0.168rem] prose-headings:text-white">
          <PortableText
            value={content.title}
            components={richTextComponent}
          />
        </div>
      )}

      <StoryFigure
        story={story}
        content={content}
        className="h-[26.293rem] rounded-[0.28rem]"
      />

      <div className="flex flex-col gap-[0.56rem] pt-[2.8rem] pb-[2rem] pl-[2rem]">
        {story.traderName && (
          <p className="mb-0 font-geist text-[2.8rem] leading-none font-medium tracking-[-0.14rem] text-white">
            {story.traderName}
          </p>
        )}
        {content.traderRole && (
          <p className="mb-0 font-geist-mono text-[1.96rem] leading-[1.33] font-semibold text-white/[0.43] uppercase">
            {content.traderRole}
          </p>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[2.125rem] p-[2rem]">
        <div className="h-px w-[calc(100%+2rem)] shrink-0 bg-white/10" />
        <div className="flex flex-col gap-[4.2rem]">
          {content.question && (
            <p className="mb-0 font-geist text-[2rem] leading-[1.567] font-medium tracking-[-0.112rem] text-white/60">
              {content.question}
            </p>
          )}
          {content.answer && (
            <div className="border-l-[0.25rem] border-[#e0ebe8] pl-[1.96rem]">
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.567] font-medium tracking-[-0.126rem] text-white">
                {content.answer}
              </p>
            </div>
          )}
        </div>
        {content.finalLink && (
          <Link
            href={content.finalLink}
            target={content.linkTarget}
            rel={content.linkRel}
            className="mt-auto flex w-fit items-center gap-[1.68rem] font-geist text-[2.24rem] leading-[1.5] font-semibold tracking-[-0.112rem] text-white transition-opacity hover:opacity-70">
            {content.ctaLabel}
            <ArrowIcon className="h-[1.4rem] w-[1.783rem] shrink-0" />
          </Link>
        )}
      </div>
    </div>
  );
});

// ─── Progress indicator (isolated to avoid re-rendering card stack) ──────────

function ProgressIndicator({ count, progress }: { count: number; progress: MotionValue<number> }) {
  const [activeIndex, setActiveIndex] = useState(0);
  useMotionValueEvent(progress, "change", (v) => {
    const next = Math.min(count - 1, Math.round(v));
    setActiveIndex((prev) => (prev === next ? prev : next));
  });
  if (count <= 1) return null;
  return (
    <div className="flex w-[4rem] shrink-0 flex-col gap-[1.75rem]">
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={cn(
            "h-[0.375rem] rounded-full transition-[width,background-color] duration-300",
            i === activeIndex ? "w-[4rem] bg-[#12f0b4]" : "w-[3rem] bg-white/20",
          )}
        />
      ))}
    </div>
  );
}

// ─── Desktop animated card ────────────────────────────────────────────────────

function buildPositions(cardHeightsPx: number[], gapPx: number): number[] {
  const positions: number[] = [];
  let cumY = 0;
  for (let i = 0; i < cardHeightsPx.length; i++) {
    positions[i] = cumY;
    cumY += (cardHeightsPx[i] ?? 0) + gapPx;
  }
  return positions;
}

function useCardTransforms(index: number, progress: MotionValue<number>, positionsRef: { current: number[] }) {
  const y = useTransform(progress, (p) => {
    const positions = positionsRef.current;
    if (!positions.length) return index === 0 ? 0 : 9999;
    const floorP = Math.max(0, Math.min(Math.floor(p), positions.length - 1));
    const fracP = p - floorP;
    const from = positions[floorP] ?? 0;
    const to = positions[Math.min(floorP + 1, positions.length - 1)] ?? from;
    const scrollPx = from + fracP * (to - from);
    return (positions[index] ?? 0) - scrollPx;
  });

  const opacity = useTransform(progress, (p) => {
    const r = index - p;
    if (r < 0) return 1;
    if (r <= 1) return 1 - r * 0.2;
    if (r <= 1.3) return ((1.3 - r) / 0.3) * 0.8;
    return 0;
  });

  const scale = useTransform(progress, (p) => {
    const r = index - p;
    if (r <= 0) return 1;
    return 1 - Math.min(r, 1) * 0.025;
  });

  return { y, opacity, scale };
}

const StoryCard = memo(function StoryCard({
  story,
  index,
  total,
  progress,
  positionsRef,
  measureRef,
  language,
}: {
  story: ITraderStory;
  index: number;
  total: number;
  progress: MotionValue<number>;
  positionsRef: { current: number[] };
  measureRef: (el: HTMLElement | null) => void;
  language?: string;
}) {
  const { y, opacity, scale } = useCardTransforms(index, progress, positionsRef);
  const [isActive, setIsActive] = useState(index === 0);
  useMotionValueEvent(progress, "change", (v) => {
    const active = Math.round(v) === index;
    setIsActive((prev) => (prev === active ? prev : active));
  });

  return (
    <motion.div
      ref={measureRef}
      style={{
        y,
        scale,
        opacity,
        zIndex: total - index,
        willChange: "transform, opacity",
      }}
      className={cn("absolute inset-x-0 top-0 flex", !isActive && "pointer-events-none")}>
      <StoryCardDesktop
        story={story}
        language={language}
      />
    </motion.div>
  );
});

// ─── Footer bar (shared) ─────────────────────────────────────────────────────

function FooterBar({ data }: { data: TradersStoriesSectionData }) {
  if (!data.button && !data.partnerText) return null;
  return (
    <div className="w-full bg-white/10">
      <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center justify-between gap-[3rem] py-[6.875rem] lg:w-[min(140rem,calc(100%-8rem))] lg:flex-row">
        <div className="flex shrink-0 items-center justify-between gap-[1.875rem]">
          <TrustpilotStars className="h-auto w-[15rem]" />
          <p className="mb-0 font-geist text-[1.75rem] whitespace-nowrap text-white">
            <span className="font-[700]">4.9</span> / 5
          </p>
          {data.partnerText && (
            <p className="mb-0 max-w-[20rem] font-geist text-[1.75rem] leading-[1.5] text-white/70 lg:max-w-[40rem] lg:text-left">
              {data.partnerText}
            </p>
          )}
        </div>
        {data.button && (
          <div className="w-full shrink-0 lg:w-fit">
            <CTAButton data={data.button} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Desktop layout (own component so useScroll reinitialises on remount) ─────

function DesktopTradersStories({
  data,
  items,
  language,
}: {
  data: TradersStoriesSectionData;
  items: ITraderStory[];
  language?: string;
}) {
  const count = items.length;
  const sectionRef = useRef<HTMLElement>(null);
  const cardElsRef = useRef<(HTMLElement | null)[]>([]);
  const cardHeightsPxRef = useRef<number[]>([]);
  const gapPxRef = useRef(GAP_REM * 8);
  const positionsRef = useRef<number[]>([]);
  const [containerH, setContainerH] = useState<string>("91rem");

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
    layoutEffect: false,
  });

  const rawProgress = useTransform(scrollYProgress, [0, 1], [0, Math.max(count - 1, 0)]);

  const smoothProgress = useSpring(rawProgress, {
    stiffness: 100,
    damping: 26,
    mass: 0.5,
    restDelta: 0.001,
    restSpeed: 0.001,
  });

  useEffect(() => {
    const recalculate = () => {
      const fs = parseFloat(window.getComputedStyle(document.documentElement).fontSize);
      gapPxRef.current = GAP_REM * fs;
      const heights = cardElsRef.current.map((el) => el?.offsetHeight ?? 0);
      if (!heights.some((h) => h > 0)) return;
      cardHeightsPxRef.current = heights;
      positionsRef.current = buildPositions(heights, gapPxRef.current);
      const maxH = Math.max(...heights);
      setContainerH(`${maxH + gapPxRef.current + maxH * PEEK_FACTOR}px`);
    };
    recalculate();
    const ro = new ResizeObserver(recalculate);
    cardElsRef.current.forEach((el) => {
      if (el) ro.observe(el);
    });
    window.addEventListener("resize", recalculate);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalculate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const measureRefs = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => (el: HTMLElement | null) => {
        cardElsRef.current[i] = el;
      }),
    [count],
  );

  const sectionHeightRem = count <= 1 ? 100 : 100 + (count - 1) * 100;

  return (
    <div className="relative w-full bg-[#001b14]">
      <section
        ref={sectionRef}
        className="relative w-full"
        style={{ minHeight: `${sectionHeightRem}rem` }}>
        <div className="sticky top-0 flex h-[135rem] flex-col gap-[8rem] overflow-clip bg-[#001b14]">
          {/* Header */}
          <div className="mx-auto flex w-[min(145rem,calc(100%-8rem))] flex-col items-center gap-[2rem] pt-[12rem] text-center">
            {data.chip && (
              <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-white/70 italic">{data.chip}</p>
            )}
            {data.title && (
              <div className="richtext [&_p]:hidden prose-em:font-blacker prose-em:text-[#d6eee7] prose-headings:text-[6rem] prose-headings:leading-[1.15] prose-headings:font-medium prose-headings:tracking-[-0.3rem] prose-headings:text-white">
                <PortableText
                  value={data.title}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {/* Cards + progress indicator — 160rem row: 4rem indicator + 4rem gap + 152rem card (Figma 1214px) */}
          <div className="flex flex-col items-center justify-center">
            <div className="relative flex w-[min(160rem,calc(100%-8rem))] items-center gap-[4rem]">
              <ProgressIndicator
                count={items.length}
                progress={smoothProgress}
              />
              <div
                className="relative min-w-0 flex-1 overflow-hidden"
                style={{ height: containerH }}>
                {items.map((story, index) => (
                  <StoryCard
                    key={story._id ?? story._key ?? index}
                    story={story}
                    index={index}
                    total={count}
                    progress={smoothProgress}
                    positionsRef={positionsRef}
                    measureRef={measureRefs[index]}
                    language={language}
                  />
                ))}
              </div>
            </div>
          </div>

          <GradualBlur
            mode="fade"
            position="bottom"
            height="24rem"
            zIndex={50}
            style={{
              background: "linear-gradient(180deg, rgba(0,27,20,0) 0%, #001b14 59.091%)",
            }}
          />
        </div>
      </section>
      {/* <FooterBar data={data} /> */}
    </div>
  );
}

// ─── Mobile layout: one card at a time, prev/next arrows + progress bar ───────

function MobileTradersStories({
  data,
  items,
  language,
}: {
  data: TradersStoriesSectionData;
  items: ITraderStory[];
  language?: string;
}) {
  const count = items.length;
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, skipSnaps: false });
  const [selected, setSelected] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(count > 1);

  useEffect(() => {
    if (!emblaApi) return;
    const sync = () => {
      setSelected(emblaApi.selectedScrollSnap());
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    sync();
    emblaApi.on("select", sync).on("reInit", sync);
    return () => {
      emblaApi.off("select", sync).off("reInit", sync);
    };
  }, [emblaApi]);

  return (
    <div className="relative w-full bg-[#001b14]">
      <div className="flex flex-col">
        <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center gap-[2rem] pt-[6rem] pb-[6rem] text-center">
          {data.chip && (
            <p className="mb-0 font-blacker text-[2rem] leading-[1.5] text-white/70 italic">{data.chip}</p>
          )}
          {data.title && (
            <div className="richtext [&_p]:hidden prose-em:font-blacker prose-em:text-[#d6eee7] prose-headings:text-[4.5rem] prose-headings:leading-[1.15] prose-headings:font-medium prose-headings:tracking-[-0.225rem] prose-headings:text-white">
              <PortableText
                value={data.title}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-[3.36rem] px-[2rem] pb-[8rem]">
          {/* Carousel viewport */}
          <div
            ref={emblaRef}
            className="overflow-hidden">
            <div className="flex touch-pan-y -ml-[2rem]">
              {items.map((story) => (
                <div
                  key={story._id ?? story._key}
                  className="flex min-w-0 flex-[0_0_100%] pl-[2rem]">
                  <StoryCardMobile
                    story={story}
                    language={language}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Pager: prev · progress · next */}
          {count > 1 && (
            <div className="flex items-center gap-[1.4545rem]">
              <button
                type="button"
                aria-label="Previous story"
                onClick={() => emblaApi?.scrollPrev()}
                disabled={!canPrev}
                className="grid h-[5.818rem] w-[5.818rem] shrink-0 place-items-center rounded-[0.242rem] border-[0.139rem] border-white/[0.14] text-white transition-opacity disabled:opacity-30">
                <ChevronIcon
                  direction="left"
                  className="h-[2.181rem] w-[2.181rem]"
                />
              </button>
              <div className="relative h-[0.25rem] min-w-0 flex-1 overflow-hidden rounded-[0.242rem] bg-white/10">
                <div
                  className="absolute inset-y-0 left-0 rounded-[0.242rem] bg-[#1aa87f] transition-[width] duration-300"
                  style={{ width: `${((selected + 1) / count) * 100}%` }}
                />
              </div>
              <button
                type="button"
                aria-label="Next story"
                onClick={() => emblaApi?.scrollNext()}
                disabled={!canNext}
                className="grid h-[5.818rem] w-[5.818rem] shrink-0 place-items-center rounded-[0.242rem] border-[0.139rem] border-white/[0.14] text-white transition-opacity disabled:opacity-30">
                <ChevronIcon
                  direction="right"
                  className="h-[2.181rem] w-[2.181rem]"
                />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* <FooterBar data={data} /> */}
    </div>
  );
}

// ─── Section ─────────────────────────────────────────────────────────────────

export default function TradersStoriesSection({
  data,
  language,
}: {
  data: TradersStoriesSectionData;
  language?: string;
}) {
  const items = data?.items ?? [];
  const isMobile = useIsMobile({ breakpoint: 1023 });

  if (!items.length && !data?.title) return null;

  if (isMobile) {
    return (
      <MobileTradersStories
        data={data}
        items={items}
        language={language}
      />
    );
  }

  return (
    <DesktopTradersStories
      data={data}
      items={items}
      language={language}
    />
  );
}
